import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, Clock, MapPin, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PageTransition } from "@/components/PageTransition";
import { AnimatedMeshGradient } from "@/components/AnimatedMeshGradient";
import { useT } from "@/lib/i18n";
import { CONTACT } from "@/constants/testIds";

export default function Contact() {
  const t = useT();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t.contact.required;
    if (!form.email.trim()) e.email = t.contact.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t.contact.invalidEmail;
    if (!form.message.trim()) e.message = t.contact.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    await new Promise((r) => setTimeout(r, 900));
    setSending(false);
    setDone(true);
    toast.success(t.contact.toastTitle, {
      description: t.contact.toastDesc,
      id: "contact-success",
    });
  };

  return (
    <PageTransition>
      {/* HERO */}
      <section className="relative paper-grain overflow-hidden" style={{ background: "var(--nexxis-bg)" }}>
        <AnimatedMeshGradient intensity={1} />
        <div className="hero-fade-bottom" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-8 pt-36 sm:pt-44 pb-12">
          <Reveal><div className="kicker mb-6">{t.contact.heroKicker}</div></Reveal>
          <div className="grid grid-cols-12 gap-8 items-end">
            <Reveal as="div" className="col-span-12 lg:col-span-8">
              <h1
                className="font-display"
                style={{
                  fontSize: "clamp(40px, 5.5vw, 76px)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  color: "var(--nexxis-ink)",
                  fontWeight: 500,
                }}
              >
                {t.contact.heroTitle}
              </h1>
            </Reveal>
            <Reveal as="div" delay={0.08} className="col-span-12 lg:col-span-4">
              <div className="h-px mb-5" style={{ background: "#FF6857", width: 48 }} />
              <p className="font-ui" style={{ color: "var(--nexxis-ink-muted)", lineHeight: 1.7, fontSize: 15 }}>
                {t.contact.heroBody}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FORM + INFO SPLIT */}
      <section className="relative" style={{ background: "var(--nexxis-bg-alt)" }}>
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-16 sm:py-20">
          <div className="grid grid-cols-12 gap-6">
            {/* LEFT: Form card */}
            <Reveal as="div" className="col-span-12 lg:col-span-7">
              <div
                className="nxx-card nxx-card--glow p-7 lg:p-10 rounded-[14px]"
                style={{
                  background: "var(--nexxis-bg)",
                  border: "1px solid var(--nexxis-border-strong)",
                }}
              >
                <div className="kicker mb-6">{t.contact.formKicker}</div>
                <form data-testid={CONTACT.form} onSubmit={onSubmit} noValidate className="space-y-5">
                  <Field label={t.contact.name} value={form.name} onChange={(v) => setForm({ ...form, name: v })} error={errors.name} testId={CONTACT.name} />
                  <Field label={t.contact.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} error={errors.email} testId={CONTACT.email} />
                  <TextArea label={t.contact.message} value={form.message} onChange={(v) => setForm({ ...form, message: v })} error={errors.message} testId={CONTACT.message} />
                  <div className="flex items-center justify-between gap-4 pt-3">
                    <div className="font-ui text-[12px]" style={{ color: "var(--nexxis-ink-muted)" }}>
                      {t.contact.respondNote}
                    </div>
                    <button
                      type="submit"
                      data-testid={CONTACT.submit}
                      disabled={sending || done}
                      className="nxx-send-btn font-ui text-[14px]"
                    >
                      <span className="nxx-send-ico">
                        <Send size={18} strokeWidth={1.8} />
                      </span>
                      <span className="nxx-send-label whitespace-nowrap">
                        {done ? t.contact.sent : sending ? t.contact.sending : t.contact.send}
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </Reveal>

            {/* RIGHT: Info card (teal) */}
            <Reveal as="div" delay={0.08} className="col-span-12 lg:col-span-5">
              <div
                className="nxx-card nxx-card--glow h-full p-7 lg:p-10 rounded-[14px] relative overflow-hidden"
                style={{ background: "#0D5F5E", color: "#F7F6F2" }}
              >
                <div className="kicker" style={{ color: "rgba(190,229,224,0.7)" }}>{t.contact.infoKicker}</div>
                <a
                  href="mailto:info@nexxishub.com"
                  data-testid={CONTACT.directEmail}
                  className="font-display block mt-4"
                  style={{ fontSize: "clamp(26px, 3vw, 38px)", color: "#F7F6F2", letterSpacing: "-0.012em", lineHeight: 1.1, fontWeight: 500 }}
                >
                  info@nexxishub.com
                </a>
                <div className="mt-5 h-px" style={{ background: "#FF6857", width: 32 }} />
                <p className="font-ui mt-5" style={{ color: "rgba(247,246,242,0.78)", lineHeight: 1.7, fontSize: 14 }}>
                  {t.contact.infoBody}
                </p>

                <div className="mt-10 grid grid-cols-1 gap-6">
                  <div className="flex items-start gap-4">
                    <Mail size={18} strokeWidth={1.4} style={{ color: "#BEE5E0", marginTop: 2 }} />
                    <div>
                      <div className="kicker" style={{ color: "rgba(190,229,224,0.7)" }}>{t.contact.emailLabel}</div>
                      <div className="font-ui mt-1 text-[14px]" style={{ color: "#F7F6F2" }}>info@nexxishub.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock size={18} strokeWidth={1.4} style={{ color: "#BEE5E0", marginTop: 2 }} />
                    <div>
                      <div className="kicker" style={{ color: "rgba(190,229,224,0.7)" }}>{t.contact.replyLabel}</div>
                      <div className="font-ui mt-1 text-[14px]" style={{ color: "#F7F6F2" }}>{t.contact.replyValue}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin size={18} strokeWidth={1.4} style={{ color: "#BEE5E0", marginTop: 2 }} />
                    <div>
                      <div className="kicker" style={{ color: "rgba(190,229,224,0.7)" }}>{t.contact.coverageLabel}</div>
                      <div className="font-ui mt-1 text-[14px]" style={{ color: "#F7F6F2" }}>{t.contact.coverageValue}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-10">
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 font-ui text-[13px]"
                    style={{ color: "rgba(247,246,242,0.9)", letterSpacing: "0.04em" }}
                  >
                    <span>{t.contact.learnAbout}</span>
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}

const inputStyle = {
  width: "100%",
  background: "var(--nexxis-bg-alt)",
  border: "1px solid var(--nexxis-border-strong)",
  borderRadius: 10,
  padding: "14px 14px",
  color: "var(--nexxis-ink)",
  fontFamily: "Inter, sans-serif",
  fontSize: 15,
  outline: "none",
};

const Field = ({ label, value, onChange, type = "text", error, testId }) => (
  <label className="block">
    <div className="flex items-center justify-between">
      <div className="kicker mb-2">{label}</div>
      {error && <div className="font-ui text-[11px]" style={{ color: "#FF6857", letterSpacing: "0.06em" }}>— {error}</div>}
    </div>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, borderColor: error ? "#FF6857" : "var(--nexxis-border-strong)" }}
    />
  </label>
);

const TextArea = ({ label, value, onChange, error, testId }) => (
  <label className="block">
    <div className="flex items-center justify-between">
      <div className="kicker mb-2">{label}</div>
      {error && <div className="font-ui text-[11px]" style={{ color: "#FF6857", letterSpacing: "0.06em" }}>— {error}</div>}
    </div>
    <textarea
      data-testid={testId}
      value={value}
      rows={5}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, borderColor: error ? "#FF6857" : "var(--nexxis-border-strong)", resize: "vertical", minHeight: 130 }}
    />
  </label>
);
